import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Boxes, Users, MapPin, Activity, Search, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import {
  statusEquipLabel, STATUS_EQUIP_BADGE,
  propriedadeLabel, tipoClienteLabel,
} from "../lib/labels";
import { DeleteButton } from "../components/DeleteButton";
import { PhotoThumb } from "../components/ImageUploader";

const fmt = (n) => new Intl.NumberFormat("pt-BR").format(n || 0);

function KpiCard({ icon: Icon, label, value, hint, color }) {
  return (
    <Card className="p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="text-xs text-zinc-500 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-zinc-900">{fmt(value)}</p>
          {hint && <p className="text-[11px] text-zinc-400 mt-0.5">{hint}</p>}
        </div>
      </div>
    </Card>
  );
}

function DistribuicaoCard({ titulo, items, total }) {
  return (
    <Card className="p-5" data-testid={`dist-${titulo.toLowerCase().replace(/\s/g, "-")}`}>
      <h3 className="text-sm font-semibold text-zinc-700 uppercase tracking-wide mb-3">{titulo}</h3>
      <div className="space-y-2">
        {items.map((it) => {
          const pct = total ? (it.n / total) * 100 : 0;
          return (
            <div key={it.label} className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="font-medium text-zinc-700">{it.display || String(it.label || "—").toUpperCase()}</span>
                <span className="text-zinc-500">{fmt(it.n)} <span className="text-zinc-400">({pct.toFixed(1)}%)</span></span>
              </div>
              <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 rounded-full" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function ClientesTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [uf, setUf] = useState("");
  const [tipo, setTipo] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 25;

  useEffect(() => {
    setLoading(true);
    api.get("/inventario/clientes", {
      params: { q, uf, tipo, limit, offset: page * limit },
    })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [q, uf, tipo, page]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Buscar por nome ou CNPJ..."
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            className="pl-9"
            data-testid="inventario-clientes-search"
          />
        </div>
        <Select value={uf || "all"} onValueChange={(v) => { setUf(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-32" data-testid="inventario-clientes-uf"><SelectValue placeholder="UF" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas UFs</SelectItem>
            {["PE","CE","AL","PB","MA","RN","PI","BA","SP","SE","RJ"].map((u) => (
              <SelectItem key={u} value={u}>{u}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={tipo || "all"} onValueChange={(v) => { setTipo(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-40" data-testid="inventario-clientes-tipo"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos tipos</SelectItem>
            <SelectItem value="ambos">AMBOS</SelectItem>
            <SelectItem value="helpdesk">HELPDESK</SelectItem>
            <SelectItem value="locativo">LOCATIVO</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden" data-testid="inventario-clientes-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="w-16">ID</TableHead>
              <TableHead>NOME FANTASIA</TableHead>
              <TableHead className="w-44">CNPJ</TableHead>
              <TableHead className="w-28">TIPO</TableHead>
              <TableHead>CIDADE / UF</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((c) => (
              <TableRow key={c.id} data-testid={`cliente-row-${c.id}`}>
                <TableCell className="font-mono text-xs text-zinc-500">{c.id}</TableCell>
                <TableCell className="font-medium">{c.nome_fantasia}</TableCell>
                <TableCell className="font-mono text-xs">{c.cnpj || "—"}</TableCell>
                <TableCell>
                  <Badge className="bg-blue-50 text-blue-700 border border-blue-200 font-mono text-[10px]">
                    {tipoClienteLabel(c.tipo_cliente)}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-zinc-700">
                  {c.cidade ? `${c.cidade} / ${c.uf || "—"}` : "—"}
                </TableCell>
                <TableCell>
                  <Link to={`/app/inventario/cliente/${c.id}`}>
                    <Button size="sm" variant="ghost" data-testid={`open-cliente-${c.id}`}>
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Frota
                    </Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-zinc-400 py-8">Nenhum cliente encontrado.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <div className="flex items-center justify-between text-sm text-zinc-600">
        <span>
          {fmt(data.total)} clientes · página {page + 1} de {Math.max(1, Math.ceil(data.total / limit))}
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="cli-prev">
            ← Anterior
          </Button>
          <Button size="sm" variant="outline" disabled={(page + 1) * limit >= data.total} onClick={() => setPage(page + 1)} data-testid="cli-next">
            Próxima →
          </Button>
        </div>
      </div>
    </div>
  );
}

function EquipamentosTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [propriedade, setPropriedade] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 25;

  useEffect(() => {
    setLoading(true);
    api.get("/inventario/equipamentos", {
      params: { q, status, propriedade, limit, offset: page * limit },
    })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [q, status, propriedade, page]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Buscar por serial ou placa..."
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            className="pl-9"
            data-testid="inventario-equip-search"
          />
        </div>
        <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-44" data-testid="inventario-equip-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            {Object.entries({disponivel:"DISPONÍVEL", reservado:"RESERVADO", locado:"LOCADO", em_atendimento:"EM ATENDIMENTO", na_oficina:"NA OFICINA", extraviado:"EXTRAVIADO"}).map(([k,v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={propriedade || "all"} onValueChange={(v) => { setPropriedade(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-44" data-testid="inventario-equip-propriedade"><SelectValue placeholder="Propriedade" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="logman">LOGMAN</SelectItem>
            <SelectItem value="jungheinrich">JUNGHEINRICH</SelectItem>
            <SelectItem value="locativo">LOCATIVO</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden" data-testid="inventario-equip-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="w-14">FOTO</TableHead>
              <TableHead className="w-16">ID</TableHead>
              <TableHead className="w-44">SERIAL</TableHead>
              <TableHead className="w-24">PLACA</TableHead>
              <TableHead className="w-40">STATUS</TableHead>
              <TableHead className="w-36">PROPRIEDADE</TableHead>
              <TableHead>OBSERVAÇÕES</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((e) => (
              <TableRow key={e.id} data-testid={`equip-row-${e.id}`}>
                <TableCell><PhotoThumb photos={e.fotos} size={36} alt={e.serial} /></TableCell>
                <TableCell className="font-mono text-xs text-zinc-500">{e.id}</TableCell>
                <TableCell className="font-mono text-xs">{e.serial}</TableCell>
                <TableCell className="font-mono text-xs">{e.placa || "—"}</TableCell>
                <TableCell>
                  <Badge className={`${STATUS_EQUIP_BADGE[e.status] || "bg-zinc-100"} font-mono text-[10px]`}>
                    {statusEquipLabel(e.status)}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-[11px] text-zinc-600">
                  {propriedadeLabel(e.propriedade)}
                </TableCell>
                <TableCell className="text-xs text-zinc-600 max-w-md truncate">{e.observacoes || "—"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Link to={`/app/inventario/equipamento/${e.id}`}>
                      <Button size="sm" variant="ghost" data-testid={`open-equip-${e.id}`}>
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> Abrir
                      </Button>
                    </Link>
                    <DeleteButton
                      url={`/inventario/equipamentos/${e.id}`}
                      label={`Equipamento ${e.serial || e.id}`}
                      warningText="Se o equipamento estiver em contrato ativo, a exclusão será bloqueada."
                      onDeleted={() => {
                        setData((prev) => ({
                          ...prev,
                          items: prev.items.filter((x) => x.id !== e.id),
                          total: Math.max(0, prev.total - 1),
                        }));
                      }}
                      testId={`del-equip-${e.id}`}
                      iconOnly
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-zinc-400 py-8">Nenhum equipamento encontrado.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <div className="flex items-center justify-between text-sm text-zinc-600">
        <span>
          {fmt(data.total)} equipamentos · página {page + 1} de {Math.max(1, Math.ceil(data.total / limit))}
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="equip-prev">← Anterior</Button>
          <Button size="sm" variant="outline" disabled={(page + 1) * limit >= data.total} onClick={() => setPage(page + 1)} data-testid="equip-next">Próxima →</Button>
        </div>
      </div>
    </div>
  );
}

export default function Inventario() {
  const [kpi, setKpi] = useState(null);

  useEffect(() => {
    api.get("/inventario/dashboard").then((r) => setKpi(r.data)).catch(() => {});
  }, []);

  const totalClientes = kpi?.totais?.clientes || 0;
  const totalEquip = kpi?.totais?.equipamentos || 0;
  const areaNeutra = kpi?.totais?.em_area_neutra || 0;

  const tipoItems = useMemo(() =>
    (kpi?.clientes_por_tipo || []).map(x => ({ ...x, display: tipoClienteLabel(x.label) })),
    [kpi]
  );
  const ufItems = kpi?.clientes_por_uf || [];
  const statusItems = useMemo(() =>
    (kpi?.equipamentos_por_status || []).map(x => ({ ...x, display: statusEquipLabel(x.label) })),
    [kpi]
  );
  const propItems = useMemo(() =>
    (kpi?.equipamentos_por_propriedade || []).map(x => ({ ...x, display: propriedadeLabel(x.label) })),
    [kpi]
  );

  return (
    <div className="space-y-6" data-testid="page-inventario">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">INVENTÁRIO LOCATIVO</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Base operacional Sprint 8 — clientes cadastrados e equipamentos em área neutra.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Users} label="CLIENTES" value={totalClientes} hint="ativos no cadastro" color="bg-blue-100 text-blue-700" />
        <KpiCard icon={Boxes} label="EQUIPAMENTOS" value={totalEquip} hint="ativos no inventário" color="bg-emerald-100 text-emerald-700" />
        <KpiCard icon={Activity} label="ÁREA NEUTRA" value={areaNeutra} hint="sem cliente vinculado" color="bg-amber-100 text-amber-700" />
        <KpiCard icon={MapPin} label="UFS OPERANDO" value={ufItems.length} hint="estados com clientes" color="bg-purple-100 text-purple-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DistribuicaoCard titulo="Clientes por tipo" items={tipoItems} total={totalClientes} />
        <DistribuicaoCard titulo="Clientes por UF" items={ufItems} total={totalClientes} />
        <DistribuicaoCard titulo="Equipamentos por status" items={statusItems} total={totalEquip} />
        <DistribuicaoCard titulo="Equipamentos por propriedade" items={propItems} total={totalEquip} />
      </div>

      <Tabs defaultValue="clientes" className="space-y-4">
        <TabsList data-testid="inventario-tabs">
          <TabsTrigger value="clientes" data-testid="tab-clientes">CLIENTES ({fmt(totalClientes)})</TabsTrigger>
          <TabsTrigger value="equipamentos" data-testid="tab-equipamentos">EQUIPAMENTOS ({fmt(totalEquip)})</TabsTrigger>
        </TabsList>
        <TabsContent value="clientes"><ClientesTab /></TabsContent>
        <TabsContent value="equipamentos"><EquipamentosTab /></TabsContent>
      </Tabs>
    </div>
  );
}
